<script setup lang="ts">
definePageMeta({ layout: 'marketing' })
</script>

<template>
  <MarketingVerticalLandingPage
    vertical="law-firms"
    accent_from="from-slate-600"
    accent_to="to-indigo-600"
    :hero="{
      eyebrow: 'For small law firms + solo practitioners',
      headline: 'Matters, court dates and client money — out of Excel, into one system.',
      subhead: 'Track every matter from intake to judgment, run conflict checks before you engage, hold client money on a proper trust ledger, and bill every tracked hour.',
      cta_text: 'See the matter workflow',
    }"
    :features="[
      { icon: '⚖️', title: 'Matter workflows per practice area', description: 'Litigation, family, property and advisory pipelines ship ready to use — intake → conflict check → engagement → hearing → judgment, each stage with its own checklist.' },
      { icon: '🛡️', title: 'Conflict-of-interest checks',        description: 'Search a person before you engage. Every prior appearance — client, opposing party, opposing counsel, witness — surfaces instantly.' },
      { icon: '🏛️', title: 'Trust (client money) ledger',        description: 'Deposits held per client, drawdowns applied straight to invoices, append-only audit trail, one-click CSV statements.' },
      { icon: '📅', title: 'Court dates on your calendar',       description: 'Hearings and filing deadlines sync to the assigned lawyer\'s Google Calendar. Reminder emails at 14, 7, 3, 1 days — you choose.' },
      { icon: '✍️', title: 'E-signed engagement letters',        description: 'Send the engagement letter for electronic signature from the matter. Signed copy lands back on the file automatically.' },
      { icon: '⏱️', title: 'Time-tracked billing',               description: 'Stopwatch on the matter. Billable hours roll into an invoice with one click — QPay, Stripe or bank transfer, synced to Xero.' },
    ]"
    :workflow_steps="[
      { step: '1', title: 'Intake + conflict check', description: 'Enquiry arrives via your intake form → conflict check runs → engagement letter goes out for e-signature.' },
      { step: '2', title: 'Act + track',             description: 'Documents collected through the client portal, court deadlines on the calendar, every hour on the clock.' },
      { step: '3', title: 'Resolve + bill',          description: 'Judgment or settlement recorded → hours become an invoice → trust drawdown settles it → statement to the client.' },
    ]"
    :pain_points="[
      'Conflict checks that mean scrolling old spreadsheets and asking around the office',
      'Court dates living in one lawyer\'s head — or a paper diary',
      'Client retainers mixed into the office account with no audit trail',
      'Billable hours reconstructed from memory at the end of the month',
    ]"
    :relief_points="[
      'Every party on every matter searchable in one conflict check',
      'Hearings + filing windows synced to calendars with layered reminders',
      'Append-only trust ledger with per-client balances and CSV statements',
      'Hours tracked on the matter, invoiced in one click',
    ]"
    :sample_screen="{
      title: 'Batbold v. Altan Construction LLC',
      subtitle: 'Matter #2026-114 · General litigation',
      items: [
        { label: 'Court',           value: 'Sukhbaatar District Civil Court' },
        { label: 'Stage',           value: 'Hearing' },
        { label: 'Next hearing',    value: '24 Jul 2026' },
        { label: 'Filing deadline', value: 'Submissions due 10 Jul' },
        { label: 'Trust balance',   value: '₮4,500,000 held' },
      ],
    }"
    :faq="[
      { q: 'Is the trust ledger compliant with law-society rules?',  a: 'It is an append-only ledger with full audit trail, per-client balances and exportable statements — the bookkeeping layer. Statutory reporting stays in your accounting package; we export, you file.' },
      { q: 'Can clients pay with QPay?',                             a: 'Yes — QPay, Stripe and bank transfer are all supported, and Mongolian tögrög (MNT) is a first-class currency.' },
      { q: 'Does it work in Mongolian?',                             a: 'The app UI ships in Mongolian and English. Client-facing email templates come in both languages out of the box.' },
      { q: 'What about court-registry integrations?',                a: 'Not yet — matters track court, case number and judge as fields on the matter, and deadlines drive the calendar. Registry integrations are on the roadmap.' },
    ]"
    pricing_callout="Sized for solo practitioners and small firms."
  />
</template>
